import API from "../utilities/LocalApiProxy";

import {
	beginReindexJob,
	beginAuditSearchIndexJob,
	beginAuditAndIndexMissingItemsJob,
	pollAdminJobToCompletion,
} from "./jobProgressActions";

import {
	CREATE_TENANT_DUPLICATE_ID_FAILURE,
	CREATE_TENANT_SUCCESS,
	EDIT_TENANT_FAILURE,
	EDIT_TENANT_SUCCESS,
	ACCESS_SEARCH_AUDIT_PAGE,
	SET_ACTIVE_OPERATION_ON_AUDIT_PAGE,
	OPEN_PERSISTENT_ERROR_SNACKBAR,
	OPEN_GLOBAL_SNACKBAR,
} from "./types";

/*
	Note:  all of the admin/ endpoints are secured via the cognito user being
	in the LexWorkplace_Admin group
*/

export function listTenants() {
	return API.post("/admin/listTenants");
}

export function fetchTenantDetail(tenantID) {
	return function (dispatch) {
		dispatch({ type: "TENANT_DETAIL_REQUEST" });
		return API.post("/admin/tenantDetail", { body: { tenantID } })
			.then((res) => {
				dispatch({ type: "TENANT_DETAIL_SUCCESS" });
				return res;
			})
			.catch((err) => {
				dispatch({ type: "TENANT_DETAIL_FAILURE" });
				return Promise.reject(err);
			});
	};
}
export function sendBulkWelcomeEmails(manualTenantID, callback) {
	return function (dispatch) {
		dispatch({
			type: "OPEN_LOADING_SNACKBAR",
			payload: { message: "Sending welcome emails to users..." },
		});
		return API.post("/admin/bulk/user/resendSuppressedWelcomeEmails", {
			body: { manualTenantID },
		})
			.then((res) => {
				// keeps polling through rate limits, rejects if the job fails
				pollAdminJobToCompletion(res.mysql.jobID)
					.then((job) => {
						const jobData = job.jobData
							? JSON.parse(job.jobData)
							: {};
						const { successCount = 0, errorCount = 0 } = jobData;
						const message =
							errorCount > 0
								? `Welcome emails sent: ${successCount} succeeded, ${errorCount} failed`
								: `Welcome emails sent to ${successCount} user${successCount === 1 ? "" : "s"}`;
						dispatch({
							type: "OPEN_GLOBAL_SNACKBAR",
							payload: {
								message,
								variant: errorCount > 0 ? "warning" : "success",
								loadingSpinnerVisible: false,
								allowClose: true,
							},
						});
					})
					.catch(() => {
						dispatch({
							type: "OPEN_GLOBAL_SNACKBAR",
							payload: {
								message: "Failed to send welcome emails",
								variant: "error",
								loadingSpinnerVisible: false,
								allowClose: true,
							},
						});
					})
					.finally(() => {
						if (callback) callback();
					});
			})
			.catch(() => {
				dispatch({
					type: "OPEN_GLOBAL_SNACKBAR",
					payload: {
						message: "Failed to start welcome email job",
						variant: "error",
						loadingSpinnerVisible: false,
						allowClose: true,
					},
				});
			});
	};
}
export function fetchUserDetail({ manualTenantID, userIdentifier }) {
	return API.get("/admin/userDetail", {
		queryParams: { manualTenantID, userIdentifier },
	});
}
export function checkIfEmailExistsInCognito(emailAddress) {
	return API.get("/admin/checkIfEmailExistsInCognito", {
		queryParams: { emailAddress },
	});
}
export function checkIfUsernameExistsInCognito(username) {
	return API.get("/admin/checkIfUsernameExistsInCognito", {
		queryParams: { username },
	});
}
export function checkIfTenantExists(manualTenantID) {
	return API.get("/admin/checkIfTenantExists", {
		queryParams: { manualTenantID },
	});
}
export function rebuildSearchIndex(tenantID) {
	return function (dispatch) {
		const successCallback = () => {
			dispatch({
				type: "REINDEX_SUCCESS",
			});
		};
		const failureCallback = () => {
			dispatch({
				type: "REINDEX_FAILURE",
			});
		};
		dispatch({
			type: "OPEN_LOADING_SNACKBAR",
			payload: { message: "Starting Search Index Rebuild..." },
		});
		return API.post("/admin/reindexChildProcess", { body: { tenantID } })
			.then((res) => {
				if (res?.mysql?.jobID) {
					beginReindexJob(
						res.mysql.jobID,
						successCallback,
						failureCallback
					)(dispatch);
				} else {
					successCallback();
				}
			})
			.catch(failureCallback);
	};
}
export function getS3BatchJobs(tenantID) {
	return function (dispatch) {
		dispatch({ type: "GET_BATCH_JOBS_REQUEST" });
		return API.post("/admin/getBatchJobs", { body: { tenantID } })
			.then((res) => {
				dispatch({
					type: "GET_BATCH_JOBS_SUCCESS",
					payload: res.mysql,
				});
				return res.mysql;
			})
			.catch((err) => {
				dispatch({ type: "GET_BATCH_JOBS_FAILURE" });
				throw err;
			});
	};
}
export function createAndRunBatchJobFromFailures(tenantID, jobID) {
	return API.post("/admin/createAndStartBatchJobFromJobFailureReport", {
		body: { tenantID, jobID },
	});
}

export function accessSearchAuditPage(tenantID) {
	return function (dispatch) {
		dispatch({ type: ACCESS_SEARCH_AUDIT_PAGE, payload: { tenantID } });
	};
}
export function setAuditPageOperation(operation) {
	return function (dispatch) {
		dispatch({
			type: SET_ACTIVE_OPERATION_ON_AUDIT_PAGE,
			payload: { operation },
		});
	};
}

export function auditSearchIndex(tenantID) {
	return function (dispatch) {
		dispatch({
			type: "OPEN_LOADING_SNACKBAR",
			payload: { message: "Starting Search Index Audit..." },
		});
		API.post("/admin/audit/checkForMissingItemsInSearchIndex", {
			body: { tenantID },
		}).then((res) => {
			if (res?.mysql?.jobID) {
				beginAuditSearchIndexJob(res.mysql.jobID)(dispatch);
			}
		});
	};
}
export function auditAndReIndexMissingItems(tenantID) {
	return function (dispatch) {
		dispatch({
			type: "OPEN_LOADING_SNACKBAR",
			payload: { message: "Starting Re-Index of Missing Items..." },
		});
		API.post("/admin/audit/reindexMissingItemsInSearchIndex", {
			body: { tenantID },
		}).then((res) => {
			if (res?.mysql?.jobID) {
				beginAuditAndIndexMissingItemsJob(res.mysql.jobID)(dispatch);
			}
		});
	};
}
export function resendWelcomeEmail(username, manualTenantID) {
	return function (dispatch) {
		dispatch({ type: "_REQUEST" });
		API.post("/admin/v2/user/resendWelcomeEmail", {
			body: { username, manualTenantID },
		})
			.then(() => {
				dispatch({ type: "_SUCCESS" });
			})
			.catch(() => {
				console.log("resend welcome email failed");
				dispatch({ type: "_FAILURE" });
			});
	};
}
export function setUserEnabledDisabled({
	newEnabled,
	username,
	sub,
	tenantID,
	successCallback,
}) {
	return function (dispatch) {
		dispatch({ type: "_REQUEST" });
		API.post(
			newEnabled
				? "/admin/user/setUserEnabled"
				: "/admin/user/setUserDisabled",
			{
				body: { username, sub, tenantID },
			}
		)
			.then(() => {
				dispatch({ type: "ENABLE_USER_SUCCESS" });
				successCallback();
			})
			.catch(() => {
				console.log("set user disabled/enabled failed");
				dispatch({ type: "_FAILURE" });
			});
	};
}
export function deleteUser(username, sub, tenantID, successCallback) {
	return function (dispatch) {
		dispatch({ type: "_REQUEST" });
		API.post("/admin/user/delete", {
			body: { username, sub, tenantID },
		})
			.then(() => {
				dispatch({ type: "DELETE_USER_SUCCESS" });
				successCallback();
			})
			.catch(() => {
				console.log("delete user failed");
				dispatch({ type: "_FAILURE" });
			});
	};
}
export function createUser(
	manualTenantID,
	username,
	displayName,
	email,
	adminStatus,
	suppressWelcomeMessage = false
) {
	return function (dispatch) {
		dispatch({ type: "_REQUEST" });
		return API.post("/admin/user/create", {
			body: {
				manualTenantID,
				email,
				username,
				displayName,
				adminStatus,
				suppressWelcomeMessage,
			},
		})
			.then((res) => {
				dispatch({ type: "_SUCCESS" });
				return res;
			})
			.catch((err) => {
				dispatch({ type: "_FAILURE" });
				return Promise.reject(err);
			});
	};
}
export function adminUpdateUser({
	username,
	emailAddress,
	displayName,
	tenantID,
}) {
	return function (dispatch) {
		API.post("/admin/updateUser", {
			body: {
				manualTenantID: tenantID,
				userData: {
					username,
					emailAddress,
					displayName,
				},
			},
		})
			.then(() => {
				window.globalHistory.push(`/admin/tenant/${tenantID}/users`);
			})
			.catch(() => {
				dispatch({
					type: OPEN_PERSISTENT_ERROR_SNACKBAR,
					payload: {
						message: "User attributes could not be updated",
					},
				});
			});
	};
}

export function adminSetEmailVerified(username, callback) {
	return function (dispatch) {
		API.post("/admin/setEmailVerified", {
			body: {
				userData: {
					username,
				},
			},
		})
			.then(() => {
				dispatch({
					type: OPEN_GLOBAL_SNACKBAR,
					payload: {
						message: `User email set to verified.  This user can now recover their password, and login via their email address`,
						variant: "success",
					},
				});
				callback && callback();
			})
			.catch((err) => {
				console.error(err);
				dispatch({
					type: OPEN_PERSISTENT_ERROR_SNACKBAR,
					payload: {
						message: "User email could not be set as verified",
					},
				});
				callback && callback();
			});
	};
}

export function createDatabaseUserRecord(
	manualTenantID,
	cognitoData,
	callback
) {
	return function (dispatch) {
		const {
			cognitoUsername,
			sub,
			cognitoDisplayName,
			cognitoEmailAddress,
		} = cognitoData;
		dispatch({ type: "_REQUEST" });
		API.post("/admin/user/createJustDatabaseUserRecord", {
			body: {
				manualTenantID,
				emailAddress: cognitoEmailAddress,
				username: cognitoUsername,
				sub,
				displayName: cognitoDisplayName,
			},
		})
			.then(() => {
				dispatch({ type: "_SUCCESS" });
				callback && callback();
			})
			.catch(() => dispatch({ type: "_FAILURE" }));
	};
}
export function createTenant({
	manualTenantID,
	name,
	regionName,
	customerID,
	usernamePrefix,
	storageLimitGB,
	archiveStorageLimitGB,
	lxwEdition,
	ocrEnabled,
	ocrPerMonthBaseline,
	literaDocumentCompareEnabled,
	inlineDocumentCompareEnabled,
	mfaIsRequiredForAllUsers,
	archiveEnabled,
}) {
	return function (dispatch) {
		dispatch({ type: "_REQUEST" });
		API.post("/admin/createTenant", {
			body: {
				manualTenantID,
				name,
				regionName,
				customerID,
				usernamePrefix,
				storageLimitGB,
				archiveStorageLimitGB,
				lxwEdition,
				ocrEnabled,
				ocrPerMonthBaseline,
				literaDocumentCompareEnabled,
				inlineDocumentCompareEnabled,
				mfaIsRequiredForAllUsers,
				archiveEnabled,
			},
		})
			.then((res) => {
				if (res?.mysql?.searchIndexCreationFailed) {
					dispatch({
						type: OPEN_PERSISTENT_ERROR_SNACKBAR,
						payload: {
							message:
								"The Search Index was not successfully created, please contact the LexWorkplace Development team",
						},
					});
				}
				dispatch({ type: CREATE_TENANT_SUCCESS });
			})
			.catch(({ lxwErrorCode }) => {
				if (lxwErrorCode === 9000) {
					dispatch({ type: CREATE_TENANT_DUPLICATE_ID_FAILURE });
				}
			});
	};
}
export function editTenant({
	manualTenantID,
	name,
	customerID,
	storageLimitGB,
	archiveStorageLimitGB,
	lxwEdition,
	ocrEnabled,
	ocrPerMonthBaseline,
	literaDocumentCompareEnabled,
	inlineDocumentCompareEnabled,
	mfaIsRequiredForAllUsers,
	archiveEnabled,
	startAdvancedPreviewFlag,
	editionAfterPreviewExpires,
	cancelAdvancedPreviewFlag,
}) {
	return function (dispatch) {
		return API.post("/admin/editTenant", {
			body: {
				manualTenantID,
				name,
				customerID,
				storageLimitGB,
				archiveStorageLimitGB,
				lxwEdition,
				ocrEnabled,
				ocrPerMonthBaseline,
				literaDocumentCompareEnabled,
				inlineDocumentCompareEnabled,
				mfaIsRequiredForAllUsers,
				archiveEnabled,
				startAdvancedPreviewFlag,
				editionAfterPreviewExpires,
				cancelAdvancedPreviewFlag,
			},
		})
			.then(() => {
				dispatch({ type: EDIT_TENANT_SUCCESS });
			})
			.catch((err) => {
				console.log(err);
				dispatch({ type: EDIT_TENANT_FAILURE });
			});
	};
}
export function getEverythingForNewTenant() {
	return function (dispatch) {
		API.get("/admin/getNextAvailableFirmID")
			.then((res) => {
				dispatch({
					type: "GET_EVERYTHING_FOR_NEW_TENANT_SUCCESS",
					payload: {
						firmDetails: { id: res.mysql.nextTenantID },
					},
				});
			})
			.catch((err) => {
				console.log(err);
			});
	};
}
export function getEverythingForEditTenant(manualTenantID) {
	return function (dispatch) {
		return API.post("/admin/tenantDetail", {
			body: { tenantID: manualTenantID },
		})
			.then((res) => {
				dispatch({
					type: "GET_EVERYTHING_FOR_EDIT_TENANT_SUCCESS",
					payload: {
						firmDetails: res.mysql.tenant,
						usageLimits: res.mysql.usageLimits,
						tenantFeatures: res.mysql.tenantFeatures,
						tenantRegion: res.mysql.tenantRegion,
						activeEditionPreview: res.mysql.activeEditionPreview,
						tenantUsage: res.mysql.tenantUsage,
						archivedItemsInfo: res.mysql.archivedItemsInfo,
					},
				});
			})
			.catch(() => {
				console.error("error getting /admin/tenantDetail");
			});
	};
}
export function unmountNewEditTenant() {
	return function (dispatch) {
		dispatch({
			type: "UNMOUNT_NEW_EDIT_TENANT",
		});
	};
}
export function setUserMFADisabled(username, successCallback) {
	return function (dispatch) {
		dispatch({ type: "_REQUEST" });
		API.post("/admin/user/setUserMFADisabled", {
			body: { username },
		})
			.then(() => {
				dispatch({ type: "DISABLED_USER_MFA_SUCCESS" });
				successCallback();
			})
			.catch(() => {
				console.log("set user MFA disabled/enabled failed");
				dispatch({ type: "_FAILURE" });
			});
	};
}
export function unlockAllDocuments({ sub, tenantID, successCallback }) {
	return function (dispatch) {
		dispatch({ type: "_REQUEST" });
		API.post("/admin/user/unlockAllDocuments", {
			body: { sub, tenantID },
		})
			.then(() => {
				dispatch({ type: "UNLOCK_ALL_DOCUMENTS_SUCCESS" });
				successCallback();
			})
			.catch(() => {
				dispatch({ type: "UNLOCK_ALL_DOCUMENTS_FAILURE" });
			});
	};
}
export function setFirmHidden(manualTenantID, firmIsHidden, successCallback) {
	return function () {
		API.post("/admin/setFirmHidden", {
			body: { manualTenantID, firmIsHidden },
		}).then(() => {
			successCallback();
		});
	};
}

export function setTenantSuspended(manualTenantID, suspend, successCallback) {
	return function () {
		API.post("/admin/setTenantSuspended", {
			body: { manualTenantID, suspend },
		}).then(() => {
			successCallback();
		});
	};
}

export function fetchRecordCounts(tenantID) {
	return API.post("/admin/recordCounts", {
		body: { tenantID },
	}).then((res) => res.mysql);
}

// The three functions below back WipeTenantDialog (see
// components/Admin/WipeTenantDialog.js), the single unified entry point for
// permanently wiping a tenant's data. Each just kicks off the existing
// backend job/operation and returns the raw API response - the dialog itself
// drives confirmation, sequencing, and progress (via pollAdminJobToCompletion
// in jobProgressActions.js) rather than a snackbar.

// Super-admin only. Schedules the S3 tenant wipe (see tenantWipe/ on the
// backend) - this only *schedules* deletion (tagged now, purged a few days
// later), it doesn't delete anything synchronously.
export function startS3TenantWipe(tenantID, expectedObjects) {
	return API.post("/admin/tenantWipe", {
		body: { tenantID, expectedObjects },
	});
}

// Super-admin only. Deletes nearly all of a tenant's DB records - see
// tenantDatabaseWipe.js on the backend for exactly what's preserved.
export function startDatabaseWipeForFirm(tenantID) {
	return API.post("/admin/wipeDatabaseForFirm", { body: { tenantID } });
}

// Deletes every OpenSearch index for a tenant outright - no recreate.
export function startSearchIndexDeleteForFirm(tenantID) {
	return API.post("/admin/deleteSearchIndexForFirm", { body: { tenantID } });
}

export function fetchDeveloperObjectInfo({
	manualTenantID,
	documentID,
	emailID,
	matterNoteID,
}) {
	const queryParams = { manualTenantID };
	if (documentID != null) queryParams.documentID = documentID;
	if (emailID != null) queryParams.emailID = emailID;
	if (matterNoteID != null) queryParams.matterNoteID = matterNoteID;
	return API.get("/admin/developer/objectInfo", { queryParams }).then(
		(res) => res.mysql
	);
}
